import React from 'react';
import classNames from 'classnames';
import { Grid, Table, TableColumnResizing, TableHeaderRow } from '@devexpress/dx-react-grid-material-ui';
import { DataTypeProvider, IntegratedSorting, SortingState } from '@devexpress/dx-react-grid';
import TableWrapper from 'shared/components/tables/TableWrapper.jsx';
import { useRecommendationsNewContext } from 'recommendationsNew/contexts/recommendationsNewContext.jsx';
import { FILTERS } from 'recommendationsNew/consts.js';
import { palette } from 'shared/constants/colorsConstants.js';
import { useUserSettingsContext } from 'users/utils/contexts/UserSettingsContext.jsx';
import { ServicesImageTag } from 'recommendationsNew/components/heatMap/heatMapFilters/servicesFilter/selectMultiMenuItem/ServicesImageTag.jsx';
import { ReactComponent as NoPotentialSavings } from 'recommendationsNew/img/savingOpportunitiesEmpty.svg';
import classes from 'recommendationsNew/components/heatMap/cloudWaste/cloudWaste.module.scss';

const getColumns = (totalsRow) => {
  return [
    { name: 'type', title: 'Type', getCellValue: (row) => ({ typeName: row.label, service: row.service }) },
    {
      name: 'savings',
      title: 'Potential Savings',
      getCellValue: (row) => row.savings,
    },
    {
      name: 'percentOfTotal',
      title: '% of Total',
      getCellValue: (row) =>
        row?.savings && row?.current ? (row.savings * 100) / totalsRow?.annualCostRecsUnimplemented : 0,
    },
    {
      name: 'numberOfRecs',
      title: 'No.',
      getCellValue: (row) => row.recsCount || 0,
    },
  ];
};
const CustomRecommendationTypeFormatter = ({ value }) => (
  <div className={classes.serviceColumnContainer}>
    <div className={classes.serviceIcon}>
      <ServicesImageTag label={value.service} />
    </div>
    <span>{value.typeName}</span>
  </div>
);

const CustomNoDataComponent = ({ isLoading }) => {
  if (!isLoading) {
    return null;
  }
  return (
    <td colSpan="100%">
      <div />
    </td>
  );
};

const CustomRowComponent = ({ row, children, filtersContext, onTypeClicked, ...restProps }) => {
  const selected = filtersContext?.find((type) => type.value === row.value);

  return (
    <Table.Row
      {...restProps}
      onClick={() => onTypeClicked({ label: row.label, value: row.value })}
      style={{ background: selected ? palette.gray[150] : '', height: '35px' }}
    >
      {children}
    </Table.Row>
  );
};

const CustomHeaderCell = ({ column, summaryRow, ...restProps }) => (
  <TableHeaderRow.Cell {...restProps} column={column}>
    <div className={classNames(classes.summaryValue, summaryRow[column?.name]?.isBold && classes.isBold)}>
      {summaryRow[column?.name]?.value}
    </div>
  </TableHeaderRow.Cell>
);
const CustomHeaderRow = ({ columns, summaryRow, ...restProps }) => {
  const { column } = restProps;
  return (
    <>
      <TableHeaderRow.Row {...restProps} column={column}>
        {restProps.children}
      </TableHeaderRow.Row>
      <TableHeaderRow.Row className={classes.summaryRowValue} {...restProps} column={column}>
        {columns.map((c) => (
          <CustomHeaderCell key={c.name} column={c} summaryRow={summaryRow} />
        ))}
      </TableHeaderRow.Row>
    </>
  );
};

const CurrencyFormatterWrapper = ({ getCurrencyNumber }) => {
  function CurrencyFormatter({ value }) {
    return <span style={{ fontWeight: 700 }}>{getCurrencyNumber(value, 2)}</span>;
  }

  return CurrencyFormatter;
};
const CustomHeaderRowWrapper = (columns, summaryRow) => {
  const Wrapper = (props) => <CustomHeaderRow {...props} columns={columns} summaryRow={summaryRow} />;
  Wrapper.displayName = 'CustomHeaderRowWrapper';
  return Wrapper;
};

const SavingsGrid = ({ data, columnsWidth, setColumnsWidth, isLoading, totalsRow }) => {
  const defaultSorting = [{ columnName: 'savings', direction: 'desc' }];
  const { getCurrencyNumber } = useUserSettingsContext();
  const { recommendationFilters: filtersContext, setRecommendationFilters: setFiltersContext } =
    useRecommendationsNewContext();

  const columns = getColumns(totalsRow);

  const summaryRow = {
    type: { value: 'Total' },
    savings: { value: getCurrencyNumber(totalsRow?.potentialAnnualSavings, 2), isBold: true },
    currentCost: { value: getCurrencyNumber(totalsRow?.annualCostRecsUnimplemented, 2), isBold: true },
    percentOfTotal: {
      value:
        totalsRow?.potentialAnnualSavings && totalsRow?.annualCostRecsUnimplemented
          ? `${((totalsRow?.potentialAnnualSavings * 100) / totalsRow?.annualCostRecsUnimplemented).toFixed(2)}%`
          : `0%`,
    },
    numberOfRecs: { value: totalsRow?.totalRecommendationsCount },
  };

  const onTypeClicked = (type) => {
    if (filtersContext[FILTERS.TYPES.id]?.find((t) => t.value === type.value)) {
      setFiltersContext({
        ...filtersContext,
        [FILTERS.TYPES.id]: filtersContext[FILTERS.TYPES.id].filter((t) => t.value !== type.value),
      });
    } else {
      setFiltersContext({
        ...filtersContext,
        [FILTERS.TYPES.id]: [type],
      });
    }
  };

  return (
    <>
      <Grid rows={data} columns={columns}>
        <SortingState defaultSorting={defaultSorting} />
        <IntegratedSorting />
        <TableWrapper
          virtual
          noDataCellComponent={(props) => <CustomNoDataComponent {...props} isLoading={isLoading} />}
          rowComponent={(props) => (
            <CustomRowComponent
              {...props}
              filtersContext={filtersContext[FILTERS.TYPES.id]}
              onTypeClicked={onTypeClicked}
            />
          )}
          height={270}
        />
        <TableColumnResizing
          resizingMode="nextColumn"
          columnWidths={columnsWidth}
          onColumnWidthsChange={setColumnsWidth}
        />
        <TableHeaderRow showSortingControls rowComponent={CustomHeaderRowWrapper(columns, summaryRow)} />
        <DataTypeProvider
          for={['type']}
          availableFilterOperations={['greaterThanOrEqual']}
          formatterComponent={CustomRecommendationTypeFormatter}
        />
        <DataTypeProvider
          for={['savings']}
          availableFilterOperations={['greaterThanOrEqual']}
          formatterComponent={CurrencyFormatterWrapper({ getCurrencyNumber })}
        />
        <DataTypeProvider
          for={['currentCost']}
          availableFilterOperations={['greaterThanOrEqual']}
          formatterComponent={CurrencyFormatterWrapper({ getCurrencyNumber })}
        />
        <DataTypeProvider
          for={['percentOfTotal']}
          availableFilterOperations={['greaterThanOrEqual']}
          formatterComponent={(data) => `${data.value.toFixed(2)}%`}
        />
      </Grid>
      {!data?.length && (
        <div className={classes.noPotentialSavingsContainer}>
          <NoPotentialSavings width={130} />
          <div className={classes.noPotentialSavingsText}>
            <div>No Potential Savings found in this view</div>
            <div className={classes.noPotentialSavingsSubText}>Try different filters</div>
          </div>
        </div>
      )}
    </>
  );
};

export default SavingsGrid;
